<template>
  <v-menu
    v-model="isMenuOpen"
    :close-on-content-click="false"
    offset-y
    left
    transition="slide-y-transition"
    :disabled="disabled"
    content-class="period-filter-dropdown__menu"
    :data-testid="`${dataTestidPrefix}__menu`"
  >
    <template v-slot:activator="{ on, attrs }">
      <button
        type="button"
        class="period-filter-dropdown__activator"
        :class="{
          'period-filter-dropdown__activator--open': isMenuOpen,
          'period-filter-dropdown__activator--disabled': disabled,
        }"
        v-bind="attrs"
        v-on="on"
        :disabled="disabled"
        :data-testid="`${dataTestidPrefix}__activator`"
      >
        <img
          src="/img/icons/fbc-icons/CalendarFilter.svg"
          alt=""
          class="period-filter-dropdown__activator-icon"
          :data-testid="`${dataTestidPrefix}__activator-icon`"
        />
        <span
          class="period-filter-dropdown__activator-label"
          :data-testid="`${dataTestidPrefix}__activator-label`"
        >
          {{ label }}
        </span>
        <span
          class="period-filter-dropdown__activator-value"
          :data-testid="`${dataTestidPrefix}__activator-value`"
        >
          {{ activatorDisplay }}
        </span>
        <v-icon
          size="14"
          class="period-filter-dropdown__activator-chevron"
          :data-testid="`${dataTestidPrefix}__activator-chevron`"
        >
          mdi-chevron-down
        </v-icon>
      </button>
    </template>

    <div class="period-filter-dropdown" :data-testid="`${dataTestidPrefix}__panel`">
      <!-- Year navigation -->
      <div class="period-filter-dropdown__year-nav" :data-testid="`${dataTestidPrefix}__year-nav`">
        <v-tooltip bottom content-class="tooltip-bottom" open-delay="500">
          <template v-slot:activator="{ on, attrs }">
            <button
              v-bind="attrs"
              v-on="on"
              type="button"
              class="period-filter-dropdown__year-btn"
              aria-label="Previous year"
              :disabled="!canGoPrevYear"
              :data-testid="`${dataTestidPrefix}__year-prev`"
              @click.stop="goPrevYear"
            >
              <v-icon size="16">mdi-chevron-left</v-icon>
            </button>
          </template>
          <span>Previous year</span>
        </v-tooltip>
        <span class="period-filter-dropdown__year" :data-testid="`${dataTestidPrefix}__year`">
          {{ viewYear }}
        </span>
        <v-tooltip bottom content-class="tooltip-bottom" open-delay="500">
          <template v-slot:activator="{ on, attrs }">
            <button
              v-bind="attrs"
              v-on="on"
              type="button"
              class="period-filter-dropdown__year-btn"
              aria-label="Next year"
              :disabled="!canGoNextYear"
              :data-testid="`${dataTestidPrefix}__year-next`"
              @click.stop="goNextYear"
            >
              <v-icon size="16">mdi-chevron-right</v-icon>
            </button>
          </template>
          <span>Next year</span>
        </v-tooltip>
      </div>

      <div class="period-filter-dropdown__divider" />

      <!-- All periods in year -->
      <button
        type="button"
        class="period-filter-dropdown__all-year"
        :class="{ 'period-filter-dropdown__all-year--active': isYearFullySelected }"
        :data-testid="`${dataTestidPrefix}__all-year`"
        @click.stop="toggleAllYear"
      >
        <v-icon size="16" class="period-filter-dropdown__all-year-icon">
          {{ isYearFullySelected ? 'mdi-check' : 'mdi-checkbox-blank-outline' }}
        </v-icon>
        <span>All periods in {{ viewYear }}</span>
      </button>

      <!-- Month grid -->
      <div class="period-filter-dropdown__months" :data-testid="`${dataTestidPrefix}__months`">
        <button
          v-for="month in months"
          :key="month.index"
          type="button"
          class="period-filter-dropdown__month"
          :class="{ 'period-filter-dropdown__month--selected': isMonthSelected(month.index) }"
          :data-testid="`${dataTestidPrefix}__month--${month.index}`"
          @click.stop="toggleMonth(month.index)"
        >
          <span
            v-if="hasMonthData(month.index)"
            class="period-filter-dropdown__month-dot"
            aria-hidden="true"
          />
          {{ month.label }}
        </button>
      </div>

      <div class="period-filter-dropdown__divider" />

      <!-- Actions -->
      <div class="period-filter-dropdown__actions" :data-testid="`${dataTestidPrefix}__actions`">
        <v-btn
          v-if="showClearAction"
          class="period-filter-dropdown__clear-btn text-none secondaryBtn"
          :data-testid="`${dataTestidPrefix}__clear-btn`"
          @click.stop="handleClear"
        >
          Clear
        </v-btn>
        <v-btn
          class="period-filter-dropdown__apply-btn text-none primaryBtn"
          :data-testid="`${dataTestidPrefix}__apply-btn`"
          @click.stop="handleApply"
        >
          Apply
        </v-btn>
      </div>
    </div>
  </v-menu>
</template>

<script>
/**
 * PeriodFilterDropdown
 *
 * Toolbar filter for year / month period tokens (`YYYY` or `YYYY-MM`).
 * Selection commits on Apply; Clear empties and closes.
 *
 * @example
 * <PeriodFilterDropdown
 *   v-model="periods"
 *   :available-month-tokens="availableMonthTokens"
 * />
 */
import './PeriodFilterDropdown.scss';
import { MONTH_SHORT_NAMES } from '../../utils/periodConstants';
import { isCustomPeriodFilter } from '../../utils/periodHelpers';

function padMonth(monthIndex1Based) {
  return String(monthIndex1Based).padStart(2, '0');
}

function monthToken(year, monthIndex1Based) {
  return `${year}-${padMonth(monthIndex1Based)}`;
}

export default {
  name: 'PeriodFilterDropdown',

  props: {
    value: {
      type: Array,
      default: () => [],
    },
    /** Month tokens that have data, e.g. ['2026-01', '2026-02'] */
    availableMonthTokens: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: 'Period',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dataTestidPrefix: {
      type: String,
      default: 'period-filter',
    },
  },

  data() {
    const nowYear = new Date().getFullYear();
    return {
      isMenuOpen: false,
      viewYear: nowYear,
      draftSelection: [],
      appliedOnClose: false,
    };
  },

  computed: {
    months() {
      return MONTH_SHORT_NAMES.map((label, idx) => ({
        index: idx + 1,
        label,
      }));
    },

    availableYearList() {
      const years = new Set();
      (this.availableMonthTokens || []).forEach((token) => {
        if (/^\d{4}-\d{2}$/.test(token)) {
          years.add(Number(token.slice(0, 4)));
        } else if (/^\d{4}$/.test(token)) {
          years.add(Number(token));
        }
      });
      (this.value || []).forEach((token) => {
        if (/^\d{4}-\d{2}$/.test(token)) {
          years.add(Number(token.slice(0, 4)));
        } else if (/^\d{4}$/.test(token)) {
          years.add(Number(token));
        }
      });
      if (!years.size) {
        years.add(new Date().getFullYear());
      }
      const sorted = Array.from(years).sort((a, b) => a - b);
      const min = sorted[0];
      const max = sorted[sorted.length - 1];
      const range = [];
      for (let y = min; y <= max; y += 1) {
        range.push(y);
      }
      return range;
    },

    minYear() {
      return this.availableYearList[0];
    },

    maxYear() {
      return this.availableYearList[this.availableYearList.length - 1];
    },

    canGoPrevYear() {
      return this.viewYear > this.minYear;
    },

    canGoNextYear() {
      return this.viewYear < this.maxYear;
    },

    isYearFullySelected() {
      const yearToken = String(this.viewYear);
      if ((this.draftSelection || []).includes(yearToken)) return true;
      // All 12 months selected for this year
      const months = [];
      for (let m = 1; m <= 12; m += 1) {
        months.push(monthToken(this.viewYear, m));
      }
      return months.every((t) => (this.draftSelection || []).includes(t));
    },

    activatorDisplay() {
      const selected = this.value || [];
      if (!selected.length) return 'All';

      const yearTokens = selected.filter((t) => /^\d{4}$/.test(t));
      const monthTokens = selected.filter((t) => /^\d{4}-\d{2}$/.test(t));

      if (yearTokens.length === 1 && monthTokens.length === 0) {
        return `${yearTokens[0]} All`;
      }
      if (yearTokens.length === 0 && monthTokens.length === 1) {
        const [y, m] = monthTokens[0].split('-');
        return `${MONTH_SHORT_NAMES[Number(m) - 1]} ${y}`;
      }
      if (yearTokens.length === 0 && monthTokens.length > 1) {
        const years = new Set(monthTokens.map((t) => t.slice(0, 4)));
        if (years.size === 1) {
          return `${monthTokens.length} months`;
        }
        return `${selected.length} selected`;
      }
      return `${selected.length} selected`;
    },

    showClearAction() {
      return isCustomPeriodFilter(this.draftSelection);
    },
  },

  watch: {
    isMenuOpen(open) {
      if (open) {
        this.syncDraftFromValue();
        this.appliedOnClose = false;
        this.ensureViewYear();
      } else if (!this.appliedOnClose) {
        // Discard draft on close without Apply
        this.draftSelection = [...(this.value || [])];
      }
    },

    value: {
      deep: true,
      handler(next) {
        if (!this.isMenuOpen) {
          this.draftSelection = [...(next || [])];
        }
      },
    },
  },

  created() {
    this.draftSelection = [...(this.value || [])];
    this.ensureViewYear();
  },

  methods: {
    ensureViewYear() {
      const years = this.availableYearList;
      if (!years.includes(this.viewYear)) {
        // Prefer current calendar year if available, else latest
        const now = new Date().getFullYear();
        this.viewYear = years.includes(now) ? now : years[years.length - 1];
      }
    },

    syncDraftFromValue() {
      this.draftSelection = [...(this.value || [])];
    },

    goPrevYear() {
      if (!this.canGoPrevYear) return;
      const idx = this.availableYearList.indexOf(this.viewYear);
      if (idx > 0) this.viewYear = this.availableYearList[idx - 1];
    },

    goNextYear() {
      if (!this.canGoNextYear) return;
      const idx = this.availableYearList.indexOf(this.viewYear);
      if (idx > -1 && idx < this.availableYearList.length - 1) {
        this.viewYear = this.availableYearList[idx + 1];
      }
    },

    hasMonthData(monthIndex1Based) {
      return (this.availableMonthTokens || []).includes(
        monthToken(this.viewYear, monthIndex1Based)
      );
    },

    isMonthSelected(monthIndex1Based) {
      const yearToken = String(this.viewYear);
      if ((this.draftSelection || []).includes(yearToken)) return true;
      return (this.draftSelection || []).includes(
        monthToken(this.viewYear, monthIndex1Based)
      );
    },

    stripYearTokens(selection, year) {
      const yearToken = String(year);
      return (selection || []).filter((t) => {
        if (t === yearToken) return false;
        if (t.startsWith(`${year}-`)) return false;
        return true;
      });
    },

    toggleAllYear() {
      const year = this.viewYear;
      const yearToken = String(year);
      if (this.isYearFullySelected) {
        this.draftSelection = this.stripYearTokens(this.draftSelection, year);
        return;
      }
      // Select whole year as a single year token
      this.draftSelection = [
        ...this.stripYearTokens(this.draftSelection, year),
        yearToken,
      ];
    },

    toggleMonth(monthIndex1Based) {
      const year = this.viewYear;
      const yearToken = String(year);
      const token = monthToken(year, monthIndex1Based);
      let next = [...(this.draftSelection || [])];

      // If year token selected, expand to all months then toggle this one off
      if (next.includes(yearToken)) {
        next = this.stripYearTokens(next, year);
        for (let m = 1; m <= 12; m += 1) {
          if (m !== monthIndex1Based) {
            next.push(monthToken(year, m));
          }
        }
        this.draftSelection = next;
        return;
      }

      const idx = next.indexOf(token);
      if (idx > -1) {
        next.splice(idx, 1);
      } else {
        next.push(token);
      }

      // If all 12 months now selected, collapse to year token
      const allMonths = [];
      for (let m = 1; m <= 12; m += 1) {
        allMonths.push(monthToken(year, m));
      }
      if (allMonths.every((t) => next.includes(t))) {
        next = [...this.stripYearTokens(next, year), yearToken];
      }

      this.draftSelection = next;
    },

    handleClear() {
      this.draftSelection = [];
      this.appliedOnClose = true;
      this.$emit('input', []);
      this.isMenuOpen = false;
    },

    handleApply() {
      this.appliedOnClose = true;
      this.$emit('input', [...this.draftSelection]);
      this.isMenuOpen = false;
    },
  },
};
</script>
